import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ObjectFactory}                                                 from 'ts-tooling';
import {filter, map, skip}                                            from 'rxjs/operators';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {
  ApDynComponentComponent
}                                                                     from '../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridColumnConfigBase,
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerConfigBase
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  FwPlanBookingComponentData,
  IFwPlanBookingEditData,
  IFwPlanBookingGridItem
}                                                                     from './plan-booking/fw-plan-booking.types';
import {
  FormStore, IEntryComponent
} from '../../stores/layout/form.store';
import {
  DriverStore
}                                                                     from '../../stores/docu/driver.store';
import {
  MachineStore
}                                                                     from '../../stores/docu/machine.store';
import {
  SettingsStore
}                                                                     from '../../stores/base-data/settings.store';
import {
  LanguageStore
}                                                                     from '../../stores/translation/language.store';
import {
  InstrumentStore
}                                                                     from '../../stores/docu/instrument.store';
import {
  ModalDialogStore
}                                                                     from '../../stores/dialog/modal.dialog.store';
import {
  IModalDialogData,
  ModalDialogButtonDisable
}                                                                     from '../../ap-interface/interfaces/ap-modaldialog-data.interface';
import {
  ApTranslationService
}                                                                     from '../../ap-utils/service/ap-translation.service';
import {
  FieldWorkPlanBooksStore
}                                                                     from '../../stores/field-work/field-work-plan-books.store';
import {
  FieldStore
}                                                                     from '../../stores/farm/field.store';
import {
  FwPlanBookingFieldsConfig
}                                                                     from './plan-booking/fw-plan-booking-fields.config';
import {
  FwPlanBookingGridConfig
}                                                                     from './plan-booking/fw-plan-booking-grid.config';
import {
  ApDynGridPagerWizardButton,
  ApDynGridPagerWizardConfig,
  ApDynGridPagerWizardSection
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-pager-wizard-config';
import {
  ApUtilService
}                                                                     from '../../ap-utils/service/ap-util.service';
import IGuid = System.IGuid;

@Component({
  selector: 'fw-plan-booking',
  template: `
    <ap-dyncomponent [headerIcon]="'ap-fungidetect-icon'"
                     [loading$]="loading$"
                     [columns]="columns$ | async"
                     [items]="items$ | async"
                     [pager]="pager"
                     [canCreate]="false"
                     (edit)="onEdit($event)">
    </ap-dyncomponent>
  `,
  standalone: false,
  providers: [
    FwPlanBookingGridConfig,
    FwPlanBookingFieldsConfig
  ]
})
export class FwPlanBookingComponent implements OnInit, AfterViewInit, OnDestroy, IEntryComponent {
  /**
   * Implements IEntryComponent
   */
  public Caption$: BehaviorSubject<string> = new BehaviorSubject('');
  @ViewChild(ApDynComponentComponent, {static: true})
  public dynComponent: ApDynComponentComponent;
  @Input() public data: FwPlanBookingComponentData;

  public columns$: Observable<ApDynGridColumnConfigBase[]> = of([]);
  public items$ = new BehaviorSubject<IFwPlanBookingGridItem[]>([]);
  public loading$: Observable<boolean> = of(true);
  public pager: ApDynGridPagerConfigBase;

  private _subscriptions: Subscription[] = []; // Stores subscriptions for cleanup

  constructor(private formStore: FormStore,
              private fieldStore: FieldStore,
              private driverStore: DriverStore,
              private machineStore: MachineStore,
              private settingsStore: SettingsStore,
              private languageStore: LanguageStore,
              private instrumentStore: InstrumentStore,
              private modalDialogStore: ModalDialogStore,
              private fieldWorkPlanBooksStore: FieldWorkPlanBooksStore,
              private translationService: ApTranslationService,
              private fwPlanBookingGridConfig: FwPlanBookingGridConfig,
              private fwPlanBookingFieldsConfig: FwPlanBookingFieldsConfig) {
  }

  /**
   * Initializes the component
   */
  public ngOnInit(): void {
    this.Caption$.next(this.data.IsBookingProcess ? 'Global__Bookings' : 'Global__Reset');
    this.setLoadingData();
    this.setGridColumns();
    this.setPager();
    this.setGridItems();
  }

  /**
   * Initializes view-dependent functionality after view initialization
   */
  public ngAfterViewInit(): void {
    this._subscriptions.push(
      this.dynComponent.SelectedItems$.subscribe((items: IFwPlanBookingGridItem[]) => {
        const fieldsToSelect = items.filter(x => x.FieldId)
          .map(i => i.FieldId.toString());
        this.fieldStore.changeSelectedField(fieldsToSelect);
      })
    );
    this._subscriptions.push(
      this.items$.subscribe((items: IFwPlanBookingGridItem[]) => {
        const itemsToSelect = items.map(i => i.Id);
        setTimeout(() => this.dynComponent.dynGrid.gridPaging.setSelectedKeys(itemsToSelect));
      })
    );
  }

  /**
   * Cleans up subscriptions on component destruction
   */
  public ngOnDestroy(): void {
    this.fieldStore.changeSelectedField([]);
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  /**
   * Handles editing of grid items
   */
  public onEdit(dataItem: IFwPlanBookingGridItem): void {
    const singleEdit = this.fwPlanBookingFieldsConfig.getEditObject(dataItem);
    const dialog = {
      title: this.translationService.translate('Global__Edit'),
      show: true,
      buttons: [
        {
          key: 'cancel',
          text: 'Global__Cancel',
          primary: false
        },
        {
          key: 'apply',
          text: 'Global__Ok',
          primary: true,
          disable: ModalDialogButtonDisable.FormInvalid
        }
      ],
      formConfig: {
        fieldSets: this.fwPlanBookingFieldsConfig.getModalDialogFields(dataItem, singleEdit,
          this.modalDialogStore.listenChanges()),
        validators: undefined,
        asyncValidators: undefined,
        values: ObjectFactory.Copy(singleEdit)
      }
    } as IModalDialogData;
    const resultSubscription = this.modalDialogStore.Listen(s => s.result)
      .pipe(skip(1))
      .subscribe((result) => {
        if (result?.key === 'apply') {
          const singleEditData = ObjectFactory.Copy(result.formValues) as IFwPlanBookingEditData;
          this.updateOverviewItemsFromSingleEdit(dataItem.Id, singleEditData);
          resultSubscription.unsubscribe();
        } else if (result?.key === 'cancel') {
          resultSubscription.unsubscribe();
        }
      });
    this.modalDialogStore.setModalDialogData(dialog);
  }

  /**
   * Sets up loading indicator based on multiple store loading states
   */
  private setLoadingData(): void {
    this.loading$ = combineLatest([
      this.fieldStore.Loading$,
      this.driverStore.Loading$,
      this.machineStore.Loading$,
      this.settingsStore.Loading$,
      this.languageStore.Loading$,
      this.instrumentStore.Loading$
    ]).pipe(
      map((loadings) => loadings.some((x) => x))
    );
  }

  /**
   * Configures grid columns based on language and settings
   */
  private setGridColumns(): void {
    this.columns$ = combineLatest([
      this.languageStore.SelectedLanguage$,
      this.settingsStore.FirstSetting$
    ]).pipe(
      filter(([language, settings]) => !!language && !!settings),
      map(([language, settings]) => this.fwPlanBookingGridConfig.getGridColumns(language, settings))
    );
  }

  /**
   * Sets up grid pager configuration with buttons and form
   */
  private setPager(): void {
    const buttonTitle = this.data.IsBookingProcess ? 'Admin_Pages_Popups__Book' : 'Global__Reset';
    this.pager = new ApDynGridPagerWizardConfig(
      new ApDynGridPagerWizardButton('Global__Cancel', ApUtilService.createEventEmitter(this.onClose.bind(this),
        this._subscriptions)
      ),
      new ApDynGridPagerWizardSection(),
      new ApDynGridPagerWizardButton(buttonTitle, ApUtilService.createEventEmitter(this.bookUnbookProcess.bind(this),
        this._subscriptions), {active: this.dynComponent.SelectedItems$.pipe(map((items) => items.length !== 0)), primary: true}
      ),
      {
        formConfig: {
          showMassEdit: false,
          onSubmit: ApUtilService.createEventEmitter(this.updateOverviewItemsFromPager.bind(this), this._subscriptions),
          config: this.fwPlanBookingFieldsConfig.getPagerEditFields()
        }
      }
    );
  }

  /**
   * Updates grid items based on pager form submission
   */
  private updateOverviewItemsFromPager(submitData: { ids: any[], value: any }): void {
    const planBookIds = submitData.ids as IGuid[];
    const editData = ObjectFactory.Copy(submitData.value) as IFwPlanBookingEditData;
    const planBooks = this.items$.getValue().filter(x => !!x);
    if (planBooks?.length > 0) {
      const updatedItems = this.items$.getValue().map((planBook: IFwPlanBookingGridItem) => {
        if (planBookIds.includes(planBook.Id)) {
          this.fwPlanBookingGridConfig.updateGridItems(planBook, editData, true);
        }
        return planBook;
      });
      setTimeout(() => this.items$.next(updatedItems));
    }
  }

  /**
   * Updates grid items based on a single item edit
   */
  private updateOverviewItemsFromSingleEdit(planBookId: IGuid, singleEditData: IFwPlanBookingEditData): void {
    const planBooks = this.items$.getValue().filter(x => !!x);
    if (planBooks?.length > 0) {
      const updatedItems = planBooks.map((planBook: IFwPlanBookingGridItem) => {
        if (planBookId === planBook.Id) {
          this.fwPlanBookingGridConfig.updateGridItems(planBook, singleEditData, false);
        }
        return planBook;
      });
      setTimeout(() => this.items$.next(updatedItems));
    }
  }

  /**
   * Initializes grid items based on field store and plan books data
   */
  private setGridItems(): void {
    this._subscriptions.push(
      combineLatest([
        this.fieldStore.Fields$,
        this.driverStore.Drivers$,
        this.machineStore.Machines$,
        this.instrumentStore.Instruments$,
        this.fieldWorkPlanBooksStore.PlanBooks$
      ]).pipe(
        filter(([fields, _, _1, _2, planBooks]) => !!fields && !!planBooks),
        map(([_, _1, _2, _3, planBooks]) => {
          return this.fwPlanBookingGridConfig.getGridItems(planBooks, this.data.PlanBooksId, this.data.IsBookingProcess);
        })
      ).subscribe((items) => {
        setTimeout(() => this.items$.next(items), 10);
      })
    );
  }

  /**
   * Handles the booking / unbooking process for selected items
   */
  private bookUnbookProcess(): void {
    const selectedItems = this.dynComponent.getSelectedItems() as IFwPlanBookingGridItem[];
    const planBooks = selectedItems.map(x => x.PlanBookCopy).filter(x => !!x);
    if (selectedItems && selectedItems.length > 0) {
      if (this.data.IsBookingProcess) {
        this.fieldWorkPlanBooksStore.bookPlanBooks(planBooks);
      } else {
        this.fieldWorkPlanBooksStore.unBookPlanBooks(planBooks);
      }
    }
    this.onClose();
  }

  /**
   * Closes the form
   */
  private onClose(): void {
    this.formStore.closeForm();
  }
}
